import { MachinesListSupportSessionsStatusTypes } from "@common/types/machines-list-types";
import {
  GatewayMachine,
  GatewayWithMachinesViewStateTypes,
  MachinesListMachineTypes,
  MachinesListCompanyEntry,
} from "@src/types/gateways-with-machines";

interface RawSkillConfigConnectionDetails {
  dsn?: string;
}

interface RawSkillConfig {
  connection_details?: RawSkillConfigConnectionDetails;
}

interface RawSkill {
  connected_via_client?: string | null;
  data_source_id?: string | null;
  type?: string;
  enabled?: boolean;
  config?: RawSkillConfig;
}

interface RawMachine {
  id: string;
  namespace: string;
  name: string;
  hostname: string | null;
  ip_address: string | null;
  serial_number: string | null;
  gateway_id: string;
  created_at: string;
  updated_at: string;
  skills?: RawSkill[];
}

interface RawGateway {
  namespace: string;
  gateway_id: string;
  can_be_claimed?: boolean;
  name: string;
  vendor_id: string | null;
  machines?: Record<string, RawMachine>;
}

export interface RawGatewaysWithMachinesResponse {
  namespace: string;
  result_index: number;
  max_result_index: number;
  gateways: Record<string, RawGateway>;
}

const normalizeSkillType = (skillType?: string): string =>
  skillType?.replace(/_/g, "").toUpperCase() || "";

const findSkill = (machine: RawMachine, skillType: string) =>
  machine.skills?.find(
    (skill) => normalizeSkillType(skill.type) === normalizeSkillType(skillType),
  );

const findOpcUaSkill = (machine: RawMachine) => findSkill(machine, "OPCUA");

const hasSkill = (machine: RawMachine, skillType: string) => {
  const skill = findSkill(machine, skillType);
  return Boolean(skill && skill.enabled !== false);
};

const getDsnFromMachine = (machine: RawMachine) => {
  const opcSkill = findOpcUaSkill(machine);

  // ponytail: fallback keeps rows renderable when OPCUA skill is missing; replace with dedicated machine->source backend mapping when available.
  // TODO: gateways/machines payload does not always include OPCUA skill; Node Manager links may fail without DSN.
  return opcSkill?.config?.connection_details?.dsn || "";
};

const getConnectedViaClientFromMachine = (machine: RawMachine) => {
  const opcSkill = findOpcUaSkill(machine);
  const hasOpcUa = hasSkill(machine, "OPCUA");

  const connectedViaClient = opcSkill?.connected_via_client || "";

  return {
    connectedViaClient,
    missingConnectedViaClient: hasOpcUa && !connectedViaClient.trim(),
  };
};

const getOpcUaDataSourceIdFromMachine = (machine: RawMachine) => {
  const opcSkill = findOpcUaSkill(machine);
  return opcSkill?.data_source_id?.trim() || "";
};

const getPortFromDsn = (dsn: string) => {
  if (!dsn) return 0;

  try {
    const parsed = new URL(dsn);
    return Number(parsed.port || 0);
  } catch {
    return 0;
  }
};

const mapMachineToGatewayMachine = (machine: RawMachine): GatewayMachine => {
  const dsn = getDsnFromMachine(machine);
  const opcUaDataSourceId = getOpcUaDataSourceIdFromMachine(machine);
  const { connectedViaClient, missingConnectedViaClient } =
    getConnectedViaClientFromMachine(machine);

  return {
    auth_info: null,
    build_info: null,
    connected_via_client: connectedViaClient,
    // TODO: OPC UA actions remain constrained until the payload includes the data-source details and nodeset.
    connection_state: "DISCONNECTED",
    created_at: machine.created_at,
    dsn,
    hostname: machine.hostname || machine.ip_address || "",
    last_active_at: machine.updated_at,
    last_updated_at: machine.updated_at,
    limits: { maximum_number_of_monitored_items: 0 },
    name: machine.name,
    namespace: machine.namespace,
    // ponytail: nodeset is unknown in gateways/machines response; Node Manager fetches it lazily after navigation.
    nodeset: null as never,
    port: getPortFromDsn(dsn),
    // Keep machine identity separate from the OPC UA data-source ID.
    machine_id: machine.id,
    ip_address: machine.ip_address,
    source_key: machine.id,
    source_type: "MACHINE_V2_PROXY",
    has_opc_ua_skill: hasSkill(machine, "OPCUA"),
    has_vnc_skill: hasSkill(machine, "VNC"),
    missing_connected_via_client: missingConnectedViaClient,
    opc_ua_data_source_id: opcUaDataSourceId,
  };
};

export const transformGatewaysWithMachinesToGatewaysList = (
  response: RawGatewaysWithMachinesResponse,
): GatewayWithMachinesViewStateTypes[] => {
  const gateways = Object.values(response?.gateways || {});

  return gateways.map((gateway) => ({
    can_be_claimed: gateway.can_be_claimed,
    gateway_uuid: gateway.gateway_id,
    name: gateway.name,
    machines: Object.values(gateway.machines || {}).map(
      mapMachineToGatewayMachine,
    ),
    sources_visible: false,
    vendor_id: gateway.vendor_id || "",
  }));
};

const mapMachineToMachinesListData = (
  machine: GatewayMachine,
  gateway: GatewayWithMachinesViewStateTypes,
): MachinesListMachineTypes => ({
  ...(machine as MachinesListMachineTypes),
  company_name: "",
  gateway_id: gateway.gateway_uuid,
  gateway_name: gateway.name,
  id: machine.machine_id,
  status: MachinesListSupportSessionsStatusTypes.INITIAL,
  vendor_id: gateway.vendor_id || "",
});

export const buildCompanyMachinesListFromGateways = (
  companyName: string,
  gateways: GatewayWithMachinesViewStateTypes[],
): Record<string, MachinesListCompanyEntry> => {
  const machines = gateways.flatMap((gateway) =>
    (gateway.machines || []).map((machine) =>
      mapMachineToMachinesListData(machine, gateway),
    ),
  );

  const machinesMap = machines.reduce<Record<string, MachinesListMachineTypes>>(
    (acc, machine) => {
      acc[machine.machine_id] = {
        ...machine,
        company_name: companyName,
      };
      return acc;
    },
    {},
  );

  return {
    [companyName]: {
      name: companyName,
      machines: machinesMap,
      sources_visible: true,
    },
  };
};
